import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Download } from "lucide-react";
import { EntitiesSection } from "./EntitiesSection";
import { EAVSection } from "./EAVSection";
import { RelationsSection } from "./RelationsSection";
import { LiveIndicator } from "@/components/ui/LiveIndicator";
import { useThematicEntities } from "@/hooks/useThematicEntities";
import { useThematicRelations } from "@/hooks/useThematicRelations";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useThematicEntityAttributes } from "@/hooks/useThematicEntityAttributes";
import { useMapGenerator } from "@/hooks/useMapGenerator";
import type { ThematicMap } from "@/types/thematicMaps";

interface KnowledgeTabProps {
  map: ThematicMap;
  activeSection: "entities" | "eav" | "relations";
}

export function KnowledgeTab({ map, activeSection }: KnowledgeTabProps) {
  const {
    entities,
    loading: entitiesLoading,
    isLive: entitiesLive,
    createEntity,
    createEntitiesBulk,
    deleteEntity,
    deleteAllEntities,
    refreshEntities,
  } = useThematicEntities(map.id);

  const {
    attributes,
    loading: attributesLoading,
    isLive: attributesLive,
    createAttribute,
    createAttributesBulk,
    deleteAttribute,
    deleteAllAttributes,
  } = useThematicEntityAttributes(map.id);

  const {
    relations,
    loading: relationsLoading,
    isLive: relationsLive,
    createRelation,
    createRelationsBulk,
    deleteRelation,
    deleteAllRelations,
  } = useThematicRelations(map.id);

  const activeLive =
    activeSection === "entities" ? entitiesLive :
    activeSection === "eav" ? attributesLive :
    relationsLive;

  const {
    nodes,
    refreshNodes: refreshNodesFn,
    updateNode,
  } = useThematicNodes(map.id);

  const {
    generating,
    currentStep,
    runEntityExtraction,
    runEAVExtraction,
    runRelationGeneration,
  } = useMapGenerator(map.id);

  const handleDeleteAllEntities = async () => {
    if (!confirm(`Usunąć WSZYSTKIE ${entities.length} encji z tej mapy? Spowoduje to też usunięcie atrybutów EAV.`)) return;
    await deleteAllEntities();
  };

  const handleDeleteAllAttributes = async () => {
    if (!confirm(`Usunąć WSZYSTKIE ${attributes.length} atrybutów EAV z tej mapy?`)) return;
    await deleteAllAttributes();
  };

  const handleDeleteAllRelations = async () => {
    if (!confirm(`Usunąć WSZYSTKIE ${relations.length} relacji z tej mapy?`)) return;
    await deleteAllRelations();
  };

  const handleExportKnowledge = () => {
    const data = {
      map: { id: map.id, name: map.name, macro_statement: map.macro_statement },
      entities: entities.map((e) => ({
        canonical_name: e.canonical_name,
        entity_type: e.entity_type,
        aliases: e.aliases,
        description: e.description,
      })),
      attributes: attributes.map((a) => ({
        entity_id: a.entity_id,
        entity_name: entities.find((e) => e.id === a.entity_id)?.canonical_name || a.entity_id,
        attribute_name: a.attribute_name,
        value: a.value,
        value_type: a.value_type,
        confidence: a.confidence,
      })),
      relations: relations.map((r) => ({
        from: nodes.find((n) => n.id === r.from_node_id)?.title || r.from_node_id,
        to: nodes.find((n) => n.id === r.to_node_id)?.title || r.to_node_id,
        type: r.relation_type,
        weight: r.weight,
      })),
      stats: {
        entities_count: entities.length,
        attributes_count: attributes.length,
        relations_count: relations.length,
        exported_at: new Date().toISOString(),
      },
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wiedza_${map.name.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Stats bar + delete buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">
            Encje: {entities.length}
          </Badge>
          <Badge variant="outline" className="text-sm px-3 py-1">
            Atrybuty: {attributes.length}
          </Badge>
          <Badge variant="outline" className="text-sm px-3 py-1">
            Relacje: {relations.length}
          </Badge>
          <LiveIndicator isLive={activeLive} />
        </div>
        <div className="flex items-center gap-2">
          {(entities.length > 0 || attributes.length > 0 || relations.length > 0) && (
            <Button variant="outline" size="sm" onClick={handleExportKnowledge}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Eksport Wiedzy (JSON)
            </Button>
          )}
          {activeSection === "entities" && entities.length > 0 && (
            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={handleDeleteAllEntities}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Usuń wszystkie encje
            </Button>
          )}
          {activeSection === "eav" && attributes.length > 0 && (
            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={handleDeleteAllAttributes}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Usuń wszystkie atrybuty
            </Button>
          )}
          {activeSection === "relations" && relations.length > 0 && (
            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={handleDeleteAllRelations}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Usuń wszystkie relacje
            </Button>
          )}
        </div>
      </div>

      {/* Active section */}
      {activeSection === "entities" && (
        <EntitiesSection
          map={map}
          entities={entities}
          entitiesLoading={entitiesLoading}
          nodes={nodes}
          generating={generating}
          currentStep={currentStep}
          runEntityExtraction={runEntityExtraction}
          onCreateEntity={createEntity}
          onCreateEntitiesBulk={createEntitiesBulk}
          onDeleteEntity={deleteEntity}
          onRefreshEntities={refreshEntities}
          onRefreshNodes={refreshNodesFn}
          onUpdateNode={updateNode}
        />
      )}

      {activeSection === "eav" && (
        <EAVSection
          map={map}
          entities={entities}
          attributes={attributes}
          attributesLoading={attributesLoading}
          nodes={nodes}
          generating={generating}
          currentStep={currentStep}
          runEAVExtraction={runEAVExtraction}
          onCreateAttribute={createAttribute}
          onCreateAttributesBulk={createAttributesBulk}
          onDeleteAttribute={deleteAttribute}
          onDeleteAllAttributes={deleteAllAttributes}
        />
      )}

      {activeSection === "relations" && (
        <RelationsSection
          map={map}
          relations={relations}
          relationsLoading={relationsLoading}
          nodes={nodes}
          generating={generating}
          currentStep={currentStep}
          runRelationGeneration={runRelationGeneration}
          onCreateRelation={createRelation}
          onCreateRelationsBulk={createRelationsBulk}
          onDeleteRelation={deleteRelation}
          onDeleteAllRelations={deleteAllRelations}
        />
      )}
    </div>
  );
}
